import {createElement} from 'react';

import classNames from 'classnames';

import type {ComponentPropsWithoutRef, ElementType, ReactElement, ReactNode, Ref} from 'react';

export type GroupProps<T extends ElementType = 'div'> = {
    children?: ReactNode,
    /**
     * The element type of the Group component. This can be any valid HTML tag
     */
    as?: T,
    display?: 'flex' | 'inline-flex',
    /**
     * Horizontal or vertical items direction
     * */
    direction?: 'h' | 'v',
    /**
     * Style justify-content
     * */
    justify?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly' | 'start' | 'end' | 'left' | 'right' | 'normal',
    /**
     * Style align-items
     * */
    alignItems?: 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch' | 'start' | 'end' | 'self-start' | 'self-end' | 'normal',
    /**
     * Style align-content
     * */
    alignContent?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly' | 'stretch' | 'start' | 'end' | 'baseline' | 'normal',
    /**
     * Style wrap
     * */
    wrap?: 'nowrap' | 'wrap' | 'wrap-reverse',
    /**
     * Indent between items
     * */
    gap?: 'none' | 'xxs' | 'xs' | 's' | 'sm' | 'm' | 'l' | 'xl' | 'xls' | 'xxl' | 'xxxl',

    /**
     * Ref to the underlying element
     */
    _ref?: Ref<T>,
} & ComponentPropsWithoutRef<T>;

/**
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/layout-group--documentation}
 * */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function Group<T extends ElementType = 'div'>(props: GroupProps<T>): ReactElement<any> {
    const {
        as = 'div',
        gap = 'm',
        direction,
        display,
        justify,
        wrap,
        alignItems,
        alignContent,
        className,
        _ref,
        ...otherProps
    } = props;

    const classes = classNames('group', `group_${gap}`, {
        group_v: direction === 'v',
        group_h: direction === 'h',
    });

    const groupStyle = {
        '--gr__d': display,
        '--gr__j': justify,
        '--gr__ai': alignItems,
        '--gr__ac': alignContent,
        '--gr__w': wrap,
    };

    // Code template for group items customization
    // const children = Children.map(props.children, (child, index) => {
    //
    //     if (child && isValidElement(child)) {
    //         return child.type === Item
    //             ? Children.only(child)
    //             : <Item key={index}>{child}</Item>;
    //     }
    //     return child;
    // });

    return createElement(as, {
        ...otherProps,
        style: {...groupStyle, ...otherProps.style},
        className: classNames(
            className,
            classes,
        ),
        ref: _ref,
    }, props.children);
}
